import type { ConfigData, AbilityConfig, MasteryDef, StatusEffectTemplate } from '../../../types';
import { escapeHtml } from './types';
import { renderMasteryIcon } from '../../components/ItemIcon';
import { buildSkillAbilityLines } from '../../components/TooltipContent';
import { elementClass } from '../../components/GameTerms';
import { Tooltip } from '../../components/Tooltip';

interface SystemCell {
  builders: string[];
  spenders: string[];
  hasAmp: boolean;
}

interface MatrixRow {
  masteryId: string;
  masteryName: string;
  cells: Record<string, SystemCell>;
  total: number;
}

interface SystemDef {
  id: string;
  label: string;
  /** Pair systems use Apply/Consume wording; resource systems use Builder/Spender. */
  isPair: boolean;
}

const SYSTEMS: SystemDef[] = [
  { id: 'tempo',     label: 'Tempo',               isPair: false },
  { id: 'fervor',    label: 'Fervor',              isPair: false },
  { id: 'break',     label: 'Break',               isPair: false },
  { id: 'fractured', label: 'Fractured / Impact',  isPair: true },
  { id: 'exposed',   label: 'Exposed / Exploit',   isPair: true },
  { id: 'stealth',   label: 'Stealth / Vanish',    isPair: true },
];

// Mastery rank-bonus stat ID -> matrix system ID (for the passive "+" amp marker)
const RANK_BONUS_STAT_TO_SYSTEM: Record<string, string> = {
  fervor_potency: 'fervor',
  break_power_increased: 'break',
  impact_damage: 'fractured',
  tempo_generation: 'tempo',
};

// ── Sort state (kept in module so it persists across re-renders) ──
type SortKey = string; // 'mastery' or system id
type SortDir = 'asc' | 'desc';
let sortKey: SortKey = 'default';
let sortDir: SortDir = 'desc';

export function setMatrixSort(key: SortKey): void {
  if (sortKey === key) {
    sortDir = sortDir === 'desc' ? 'asc' : 'desc';
  } else {
    sortKey = key;
    sortDir = key === 'mastery' ? 'asc' : 'desc';
  }
}

function emptyCell(): SystemCell {
  return { builders: [], spenders: [], hasAmp: false };
}

function cellTotal(cell: SystemCell): number {
  return cell.builders.length + cell.spenders.length + (cell.hasAmp ? 1 : 0);
}

function deriveRows(config: ConfigData): MatrixRow[] {
  const masteries: MasteryDef[] = Object.values(config.masteries).filter(m => m.status !== 'nyi');

  const abilitiesByMastery: Record<string, AbilityConfig[]> = {};
  for (const ability of Object.values(config.abilities)) {
    if (!ability.masteryId) continue;
    if (!abilitiesByMastery[ability.masteryId]) abilitiesByMastery[ability.masteryId] = [];
    abilitiesByMastery[ability.masteryId].push(ability);
  }

  const statusEffects = config.statusEffects as Record<string, StatusEffectTemplate>;
  const _barrierEffects = new Set<string>(); // (kept for future re-add; barrier currently not a column)
  for (const [id, effect] of Object.entries(statusEffects)) {
    if (effect?.grantsBarrier) _barrierEffects.add(id);
  }

  const rows: MatrixRow[] = [];

  for (const m of masteries) {
    const cells: Record<string, SystemCell> = {};
    for (const sys of SYSTEMS) cells[sys.id] = emptyCell();

    // ── Rank-bonus passive amp ────────────────────────────────
    const rb = m.rankBonus;
    if (rb) {
      const sysId = RANK_BONUS_STAT_TO_SYSTEM[rb.stat];
      if (sysId) cells[sysId].hasAmp = true;
      if (rb.type === 'tagIncreased' && rb.stat === 'impact') {
        cells.fractured.hasAmp = true;
      }
    }

    // ── Per-ability scan ──────────────────────────────────────
    // `creditId` lets summon abilities credit their minion's levers under the summoner ability.
    // The badge points at the summon ability (the player-facing card), not the minion's internal
    // ability — that way clicking the cell opens the summon tooltip, which already shows the
    // minion's ability inline.
    const scanAbility = (a: AbilityConfig, creditId: string): void => {
      const tags = a.tags || [];

      // Tempo
      if (a.tempoGenerate || a.tempoGenerateFromStealth || a.tempoOnCrit) {
        cells.tempo.builders.push(creditId);
      }
      if (
        a.tempoConsumeBonus || a.tempoConsumeStun || a.tempoConsumeCrit ||
        a.tempoConsumeCritMulti || a.tempoConsumePen || a.tempoConsumeLeech ||
        a.tempoConsumeHits || a.tempoConsumeChain
      ) {
        cells.tempo.spenders.push(creditId);
      }

      // Traps — passive spenders. The consume logic lives on the applied status template's
      // `trapTriggerCondition`, not on the ability itself, so we resolve through it.
      if (a.applyTargetEffect) {
        const tpl = statusEffects[a.applyTargetEffect];
        if (tpl?.trapTriggerCondition === 'tempo_present') {
          cells.tempo.spenders.push(creditId);
        } else if (tpl?.trapTriggerCondition === 'broken_state') {
          cells['break'].spenders.push(creditId);
        }
      }

      // Fervor
      if (
        a.fervorOnKill || a.fervorOnCast || a.fervorOnSuperEffective ||
        a.fervorOnStealth || a.fervorOnImprint || a.fervorOnCrit || a.fervorOnDotConsume ||
        a.applySelfEffect === 'defy'
      ) {
        cells.fervor.builders.push(creditId);
      }
      if (a.fervorConsumeBonus) cells.fervor.spenders.push(creditId);

      // Break — breakPowerMax > 0 means the ability contributes to the fill bar; bonusVsBroken consumes the state.
      if (a.breakPowerMax) cells['break'].builders.push(creditId);
      if (a.bonusVsBroken) cells['break'].spenders.push(creditId);

      // Fractured / Impact
      if (a.applyTargetEffect === 'fractured') cells.fractured.builders.push(creditId);
      if (tags.includes('impact')) cells.fractured.spenders.push(creditId);

      // Exposed / Exploit
      if (a.applyTargetEffect === 'exposed') cells.exposed.builders.push(creditId);
      if (tags.includes('exploit')) cells.exposed.spenders.push(creditId);

      // Stealth / Vanish
      if (a.applySelfEffect === 'vanish') cells.stealth.builders.push(creditId);
      if (a.tempoGenerateFromStealth || a.fervorOnStealth) {
        cells.stealth.spenders.push(creditId);
      }
    };

    const myAbilities = abilitiesByMastery[m.id] || [];
    for (const a of myAbilities) {
      scanAbility(a, a.id);

      // Summon rollup — credit the minion's ability levers to the summon ability's row.
      // Surfaces Tremor Totem's Break contribution (resonance) under Manifestation, etc.
      if (a.summonTemplateId) {
        const template = config.minionTemplates?.[a.summonTemplateId];
        if (template) {
          for (const minionAbilityId of template.abilityIds) {
            const minionAbility = config.abilities[minionAbilityId];
            if (minionAbility) scanAbility(minionAbility, a.id);
          }
        }
      }
    }

    // Total interactions across the matrix (used for default sort)
    let total = 0;
    for (const sys of SYSTEMS) total += cellTotal(cells[sys.id]);

    rows.push({
      masteryId: m.id,
      masteryName: m.name,
      cells,
      total,
    });
  }

  // ── Apply sort ────────────────────────────────────────────────
  if (sortKey === 'mastery') {
    rows.sort((a, b) => a.masteryName.localeCompare(b.masteryName));
    if (sortDir === 'desc') rows.reverse();
  } else if (sortKey !== 'default') {
    rows.sort((a, b) => cellTotal(b.cells[sortKey]) - cellTotal(a.cells[sortKey]));
    if (sortDir === 'asc') rows.reverse();
  }

  return rows;
}

function renderCell(cell: SystemCell, sysId: string): string {
  const sys = SYSTEMS.find(s => s.id === sysId)!;
  const parts: string[] = [];

  if (cell.builders.length) {
    const label = sys.isPair ? 'A' : 'B';
    const ids = cell.builders.join(',');
    parts.push(
      `<span class="codex-matrix-role codex-matrix-role--builder" data-matrix-abilities="${escapeHtml(ids)}">${label}${cell.builders.length}</span>`
    );
  }
  if (cell.spenders.length) {
    const label = sys.isPair ? 'C' : 'S';
    const ids = cell.spenders.join(',');
    parts.push(
      `<span class="codex-matrix-role codex-matrix-role--spender" data-matrix-abilities="${escapeHtml(ids)}">${label}${cell.spenders.length}</span>`
    );
  }
  if (cell.hasAmp) {
    parts.push(
      `<span class="codex-matrix-role codex-matrix-role--amp" title="Mastery rank bonus boosts this system">+</span>`
    );
  }

  return parts.length
    ? `<td class="codex-matrix-cell">${parts.join(' ')}</td>`
    : `<td class="codex-matrix-cell"><span class="codex-matrix-empty">—</span></td>`;
}

/** Build a multi-ability hover tooltip for a matrix badge. Mirrors the Skills chapter card. */
function buildMatrixCellTooltip(abilityIds: string[], config: ConfigData): string {
  const blocks: string[] = [];
  for (const id of abilityIds) {
    const ability = config.abilities[id];
    if (!ability) continue;
    const skillDef = config.skillDefs
      ? Object.values(config.skillDefs).find(s => s.abilityId === id)
      : undefined;
    const rank1 = skillDef?.ranks?.[0];
    const elCls = elementClass(ability.element);
    const elLabel = ability.element || '';
    const lines = buildSkillAbilityLines(ability, rank1, config);
    blocks.push(`
      <div class="tooltip-inner">
        <div class="tooltip-header">
          <span class="tooltip-name">${escapeHtml(ability.name)}</span>
          ${elLabel ? `<span class="tooltip-element ${elCls}">${escapeHtml(elLabel)}</span>` : ''}
        </div>
        ${lines.join('')}
      </div>
    `);
  }
  if (!blocks.length) return '';
  // Single ability — render as a normal tooltip (no grid, default narrow width).
  if (blocks.length === 1) return blocks[0];
  // Multi-ability — grid that lays cards side-by-side. `:has()` in codex.css bumps the
  // outer #tooltip max-width when this grid is the content.
  return `<div class="codex-matrix-tooltip-grid">${blocks.join('')}</div>`;
}

/** Wire up Tooltip listeners on every matrix badge. Call after the matrix HTML is mounted. */
export function bindMasterySystemMatrix(content: HTMLElement, config: ConfigData): void {
  content.querySelectorAll<HTMLElement>('[data-matrix-abilities]').forEach(el => {
    const idList = el.dataset.matrixAbilities;
    if (!idList) return;
    const ids = idList.split(',').filter(Boolean);
    if (!ids.length) return;
    Tooltip.attach(el, () => buildMatrixCellTooltip(ids, config));
  });
}

function sortIndicator(key: SortKey): string {
  if (sortKey !== key) return '';
  return sortDir === 'desc' ? ' ▼' : ' ▲';
}

export function renderMasterySystemMatrix(config: ConfigData): string {
  const rows = deriveRows(config);

  // Compute footer totals across all masteries for each system
  const totals: Record<string, { builders: number; spenders: number; amps: number }> = {};
  for (const sys of SYSTEMS) totals[sys.id] = { builders: 0, spenders: 0, amps: 0 };
  for (const row of rows) {
    for (const sys of SYSTEMS) {
      totals[sys.id].builders += row.cells[sys.id].builders.length;
      totals[sys.id].spenders += row.cells[sys.id].spenders.length;
      if (row.cells[sys.id].hasAmp) totals[sys.id].amps += 1;
    }
  }

  const masteryHeader = `
    <th class="codex-matrix-mastery-header codex-matrix-sortable" data-matrix-sort="mastery">
      Mastery${sortIndicator('mastery')}
    </th>
  `;

  const systemHeaders = SYSTEMS.map(s =>
    `<th class="codex-matrix-system-header codex-matrix-sortable" data-matrix-sort="${s.id}">${s.label}${sortIndicator(s.id)}</th>`
  ).join('');

  const body = rows.map(row => {
    const cellsHtml = SYSTEMS.map(sys => renderCell(row.cells[sys.id], sys.id)).join('');
    return `
      <tr class="codex-matrix-row">
        <td class="codex-matrix-mastery-name">
          <span class="codex-matrix-mastery-icon">${renderMasteryIcon(row.masteryId, 'sm')}</span>
          ${escapeHtml(row.masteryName)}
        </td>
        ${cellsHtml}
      </tr>
    `;
  }).join('');

  const footerCells = SYSTEMS.map(sys => {
    const t = totals[sys.id];
    const total = t.builders + t.spenders + t.amps;
    if (total === 0) {
      return `<td class="codex-matrix-cell codex-matrix-footer-cell"><span class="codex-matrix-empty">—</span></td>`;
    }
    const parts: string[] = [];
    const buildLabel = sys.isPair ? 'A' : 'B';
    const spendLabel = sys.isPair ? 'C' : 'S';
    if (t.builders > 0) {
      const tip = sys.isPair ? `${t.builders} appliers` : `${t.builders} builders`;
      parts.push(`<span class="codex-matrix-role codex-matrix-role--builder" title="${tip}">${buildLabel}${t.builders}</span>`);
    }
    if (t.spenders > 0) {
      const tip = sys.isPair ? `${t.spenders} consumer abilities (tagged)` : `${t.spenders} spenders`;
      parts.push(`<span class="codex-matrix-role codex-matrix-role--spender" title="${tip}">${spendLabel}${t.spenders}</span>`);
    }
    if (t.amps > 0) {
      parts.push(`<span class="codex-matrix-role codex-matrix-role--amp" title="${t.amps} masteries with passive amp">+${t.amps}</span>`);
    }
    return `<td class="codex-matrix-cell codex-matrix-footer-cell">${parts.join(' ')}</td>`;
  }).join('');

  return `
    <div class="codex-matrix-section">
      <div class="codex-matrix-title">Mastery × System Matrix</div>
      <div class="codex-matrix-subtitle">
        Build-around systems each mastery touches, derived from skill data. Hover any badge for the ability list. Click a header to sort.
      </div>
      <div class="codex-matrix-legend">
        <span class="codex-matrix-legend-item">
          <span class="codex-matrix-role codex-matrix-role--builder">B</span>
          Builder <span class="codex-matrix-legend-dim">(generates)</span>
        </span>
        <span class="codex-matrix-legend-item">
          <span class="codex-matrix-role codex-matrix-role--spender">S</span>
          Spender <span class="codex-matrix-legend-dim">(consumes)</span>
        </span>
        <span class="codex-matrix-legend-item">
          <span class="codex-matrix-role codex-matrix-role--builder">A</span>
          Apply <span class="codex-matrix-legend-dim">(grants the debuff)</span>
        </span>
        <span class="codex-matrix-legend-item">
          <span class="codex-matrix-role codex-matrix-role--spender">C</span>
          Consume <span class="codex-matrix-legend-dim">(tagged ability benefits)</span>
        </span>
        <span class="codex-matrix-legend-item">
          <span class="codex-matrix-role codex-matrix-role--amp">+</span>
          Passive <span class="codex-matrix-legend-dim">(rank bonus)</span>
        </span>
      </div>
      <div class="codex-matrix-scroll">
        <table class="codex-matrix-table">
          <thead>
            <tr>
              ${masteryHeader}
              ${systemHeaders}
            </tr>
          </thead>
          <tbody>${body}</tbody>
          <tfoot>
            <tr class="codex-matrix-footer-row">
              <td class="codex-matrix-mastery-name codex-matrix-footer-cell">Total abilities</td>
              ${footerCells}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  `;
}
